/** The booking record's section registry (plan §2.1–§2.3, §5, D1): every catalog field key
 *  → where it sits — its spine panel (`stage`), its tabs-view tab (`facet`), its position
 *  and its block (the Editable fields, the muted "From <restech>" vendor block, or the
 *  System block of derived / provenance values).
 *
 *  Both views render this one registry; neither places a field the registry does not
 *  know. The tabs view lays each facet out as the spine lays the whole registry out —
 *  one section per stage the facet reaches — so a field keeps its block, its order
 *  and its panel's renderings in both. Stage and facet mirror the backend catalog (`booking_changeset/schema.py`);
 *  the order is the console's — in the Guest & pre-check-in panel the guest's own
 *  profile comes first, then this booking's travellers and pre-check-in.
 *
 *  Contract (registry.contract.test.ts): the keys are exactly the spec enum
 *  `BookingRecordFieldKey`, so a field the backend adds fails console CI until it is
 *  placed here. Action fields (on-site status, pre-check-in state, owner) sit in the
 *  Editable block — they change through actions queued in the draft. */
import { BOOKING_RECORD_STAGES, type BookingRecordFacet, type BookingRecordStage } from '@/api/bookingRecord';
import type { FieldBlockKind, FieldRegistry, RegistryEntry } from '@/features/records/views/types';

export type BookingRegistryEntry = RegistryEntry<BookingRecordStage, BookingRecordFacet>;

function at(
  stage: BookingRecordStage,
  facet: BookingRecordFacet,
  block: FieldBlockKind,
  order: number,
): BookingRegistryEntry {
  return { stage, facet, block, order };
}

const E = 'editable';
const V = 'vendor';
const S = 'system';

export const BOOKING_RECORD_REGISTRY = {
  // ---------------------------------------------------------- Booked & order
  booking_state: at('booked', 'booking', E, 10),
  order_ref: at('booked', 'booking', E, 20),
  supplier_ref: at('booked', 'booking', E, 30),
  reseller_ref: at('booked', 'booking', E, 40),
  affiliate_ext_id: at('booked', 'booking', E, 50),
  role: at('booked', 'booking', E, 60),
  package_id: at('booked', 'booking', E, 70),
  package_title: at('booked', 'booking', E, 80),
  package_description: at('booked', 'booking', E, 90),
  tags: at('booked', 'booking', E, 100),
  custom_id: at('booked', 'booking', V, 110),
  product_custom_id: at('booked', 'booking', V, 120),
  prev_custom_id: at('booked', 'booking', V, 130),
  booking_ts: at('booked', 'booking', V, 140),
  pricing: at('booked', 'money', V, 150),
  promo: at('booked', 'money', V, 160),
  settlement_method: at('booked', 'money', V, 170),
  email_receipt: at('booked', 'money', V, 180),
  tickets: at('booked', 'money', V, 190),
  extras: at('booked', 'money', V, 200),
  voucher: at('booked', 'money', V, 210),
  invoice: at('booked', 'money', V, 220),
  attachments: at('booked', 'money', V, 230),
  order: at('booked', 'booking', S, 240),
  public_id: at('booked', 'booking', S, 250),
  integration: at('booked', 'integration', S, 260),
  restech_account: at('booked', 'integration', S, 270),
  booking_system: at('booked', 'integration', S, 280),
  create_source: at('booked', 'integration', S, 290),
  created_ts: at('booked', 'integration', S, 300),
  update_source: at('booked', 'integration', S, 310),
  updated_ts: at('booked', 'integration', S, 320),

  // ---------------------------------------------------- Guest & pre-check-in
  // The guest (users.User) — affects every booking of the guest.
  first_name: at('precheckin', 'guest', E, 10),
  last_name: at('precheckin', 'guest', E, 20),
  lang: at('precheckin', 'guest', E, 30),
  country: at('precheckin', 'guest', E, 40),
  // Email and phone are edited through the contacts list (O8) — a full-width row.
  contacts: at('precheckin', 'guest', E, 50),
  freeze_email: at('precheckin', 'guest', E, 70),
  freeze_phone: at('precheckin', 'guest', E, 80),
  allow_email_nf: at('precheckin', 'guest', E, 90),
  allow_sms_nf: at('precheckin', 'guest', E, 100),
  allow_whatsapp_nf: at('precheckin', 'guest', E, 110),
  date_of_birth: at('precheckin', 'guest', E, 120),
  passport_number: at('precheckin', 'guest', E, 130),
  address: at('precheckin', 'guest', E, 140),
  // This booking: its owner, travellers and the lead's pre-check-in.
  owner: at('precheckin', 'guest', E, 150),
  people_count: at('precheckin', 'guest', E, 160),
  adult_count: at('precheckin', 'guest', E, 170),
  children_count: at('precheckin', 'guest', E, 180),
  infant_count: at('precheckin', 'guest', E, 190),
  is_minor: at('precheckin', 'guest', E, 200),
  emergency_contact: at('precheckin', 'guest', E, 210),
  custom_note: at('precheckin', 'guest', E, 220),
  precheckin_state: at('precheckin', 'guest', E, 230),
  precheckin_tags: at('precheckin', 'guest', E, 240),
  waivers: at('precheckin', 'guest', V, 250),
  ota: at('precheckin', 'guest', V, 260),
  bookingmgmt_link: at('precheckin', 'guest', S, 270),
  email_verified: at('precheckin', 'guest', S, 290),
  email_is_fake: at('precheckin', 'guest', S, 300),
  app_platform: at('precheckin', 'guest', S, 310),
  twilio_identity: at('precheckin', 'guest', S, 320),

  // ---------------------------------------------------------------- Departure
  tour: at('departure', 'booking', E, 10),
  date: at('departure', 'booking', E, 20),
  departure_time: at('departure', 'booking', E, 30),
  end_ts_type: at('departure', 'booking', E, 40),
  end_ts: at('departure', 'booking', E, 50),
  departure_key: at('departure', 'booking', E, 60),
  departure_key_locked: at('departure', 'booking', E, 70),
  booking_notes: at('departure', 'booking', E, 80),
  all_day: at('departure', 'booking', V, 90),
  start_ts: at('departure', 'booking', S, 100),
  timezone: at('departure', 'booking', S, 110),
  is_bespoke: at('departure', 'booking', S, 120),
  departure: at('departure', 'booking', S, 130),

  // ------------------------------------------------------------------ On-site
  checkin_status: at('onsite', 'checkin', E, 10),
  checkin_notes: at('onsite', 'checkin', E, 20),
  ticket_redemption: at('onsite', 'checkin', V, 30),
  checkin_source: at('onsite', 'checkin', S, 40),
  checkin_ts: at('onsite', 'checkin', S, 50),
  checkin_error: at('onsite', 'checkin', S, 60),
  checkin_url: at('onsite', 'checkin', S, 70),
  checkin_window: at('onsite', 'checkin', S, 80),
  in_manifest: at('onsite', 'checkin', S, 90),

  // --------------------------------------------------------------- After trip
  survey_state: at('after', 'booking', E, 10),
  reviews: at('after', 'activity', V, 20),

  // ------------------------------------------------------- Communication lane
  mytrips_display: at('communication', 'communication', E, 10),
  exclude_from_chat: at('communication', 'communication', E, 20),
  exclude_from_group_chat: at('communication', 'communication', E, 30),
  pause_all_notifications: at('communication', 'communication', E, 40),
  pause_pre_and_post_trip_notifications: at('communication', 'communication', E, 50),
} as const satisfies FieldRegistry<BookingRecordStage, BookingRecordFacet>;

/** Every catalog key the console places — the spec enum `BookingRecordFieldKey`. */
export type BookingRecordFieldKey = keyof typeof BOOKING_RECORD_REGISTRY;

export type BookingRegistry = Readonly<Record<string, BookingRegistryEntry>>;

/** The spine's five lifecycle stages (the Communication lane is not a spine node). */
export const SPINE_STAGES = ['booked', 'precheckin', 'departure', 'onsite', 'after'] as const satisfies readonly BookingRecordStage[];
export type SpineStage = (typeof SPINE_STAGES)[number];

/** Panel titles, in lifecycle order (the spine's panels, then the lane). */
export const STAGE_TITLES: Record<BookingRecordStage, string> = {
  booked: 'Booked & order',
  precheckin: 'Guest & pre-check-in',
  departure: 'Departure',
  onsite: 'On-site',
  after: 'After trip',
  communication: 'Communication',
};

/** The tabs view's tab labels, in `BOOKING_RECORD_FACETS` order. */
export const FACET_TITLES: Record<BookingRecordFacet, string> = {
  booking: 'Booking',
  guest: 'Guest',
  money: 'Money',
  checkin: 'Check-in',
  integration: 'Integration',
  activity: 'Activity',
  communication: 'Communication',
};

/** The registry's entries of one facet — what one tab of the tabs view lays out. */
export function facetRegistry(registry: BookingRegistry, facet: BookingRecordFacet): BookingRegistry {
  return Object.fromEntries(Object.entries(registry).filter(([, e]) => e.facet === facet));
}

/** The keys a view groups by `by` into one section, ordered by stage then position —
 *  the placement both views render. Pure. */
export function placements<K extends 'stage' | 'facet'>(
  registry: BookingRegistry,
  by: K,
): Map<BookingRegistryEntry[K], string[]> {
  const stageIndex = (s: BookingRecordStage) => BOOKING_RECORD_STAGES.indexOf(s);
  const keys = Object.keys(registry).sort((a, b) => {
    const x = registry[a]!;
    const y = registry[b]!;
    return stageIndex(x.stage) - stageIndex(y.stage) || x.order - y.order;
  });
  const out = new Map<BookingRegistryEntry[K], string[]>();
  for (const key of keys) {
    const group = registry[key]![by];
    const list = out.get(group);
    if (list) list.push(key);
    else out.set(group, [key]);
  }
  return out;
}
